import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// 1. Create Context
export const ResponsiveContext = createContext<string[] | null>(null);

const getLayoutMode = () => {
    let returnLayout = '';
    let returnScreenSize = '';

    if (window.innerWidth >= 768 && window.innerWidth < 992) {
        returnLayout = 'tablet';
        returnScreenSize = 'sm';
    } else if (window.innerWidth >= 992 && window.innerWidth < 1280) {
        returnLayout = 'desktop';
        returnScreenSize = 'md';
    }
    else if (window.innerWidth >= 1280 && window.innerWidth < 1440) {
        returnLayout = 'desktop';
        returnScreenSize = 'lg';
    } else if (window.innerWidth >= 1440) {
        returnLayout = 'desktop';
        returnScreenSize = 'xl';
    } else {
        returnLayout = 'mobile';
        returnScreenSize = 'xs';
    }

    return [returnLayout, returnScreenSize];
};

// 2. Create custom hook
export const useResponsiveContext = () => {
    const context = useContext(ResponsiveContext);

    if (!context) {
        throw new Error('useResponsiveContext must be used within an ResponsiveProvider');
    }
    return context;
};

// 3. Create Provider
export const ResponsiveProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const initialSize = getLayoutMode()[0];
    const initialScreenSize = getLayoutMode()[1];
    const [viewMode, setViewMode] = useState<string | null>(initialSize);
    const [screenSize, setScreenSize] = useState<string | null>(initialScreenSize);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const handleResizeEvent = useCallback(() => {
        if (windowWidth !== window.innerWidth) {
            const currentViewMode = viewMode;

            if (window.innerWidth >= 768 && window.innerWidth < 992) {
                if (currentViewMode !== 'tablet') setViewMode('tablet');
                if (currentViewMode !== 'sm') setScreenSize('sm');
            } else if (window.innerWidth >= 992 && window.innerWidth < 1280) {
                if (currentViewMode !== 'desktop') setViewMode('desktop');
                if (currentViewMode !== 'md') setScreenSize('md');
            }
            else if (window.innerWidth >= 1280 && window.innerWidth < 1440) {
                if (currentViewMode !== 'desktop') setViewMode('desktop');
                if (currentViewMode !== 'lg') setScreenSize('lg');
            } else if (window.innerWidth >= 1440) {
                if (currentViewMode !== 'desktop') setViewMode('desktop');
                if (currentViewMode !== 'xl') setScreenSize('xl');
            } else {
                if (currentViewMode !== 'mobile') setViewMode('mobile');
                if (currentViewMode !== 'xs') setScreenSize('xs');
            }

            setWindowWidth(window.innerWidth);
        }
    }, [viewMode, windowWidth]);

    useEffect(() => {
        window.addEventListener('resize', handleResizeEvent);

        return () => {
            window.removeEventListener('resize', handleResizeEvent);
        };
    }, [handleResizeEvent]);

    const value = useMemo(() => {
        return [viewMode!, screenSize!];
    }, [screenSize, viewMode]);

    return <ResponsiveContext.Provider value={value}>{children}</ResponsiveContext.Provider>;
};